import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, signOut } from '../AuthContext';
import { useOrg } from '../data/orgs';
import AccountModal from './AccountModal';
import Icon from './Icons';
import { openCommandBar } from './CommandBar';
import { openGuide } from './GuidePanel';

const APP_NAME = process.env.REACT_APP_APP_NAME || 'fast.legal';

export const initialsOf = (name) =>
  String(name || '?')
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('') || '?';

export default function Navbar() {
  const { user } = useAuth();
  const { org, memberships, setOrgId, role } = useOrg();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Collapse the mobile menu + user menu whenever we navigate to a new route.
  useEffect(() => {
    setOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  // The sidebar's Settings item opens the same account modal.
  useEffect(() => {
    const onAccount = () => setShowAccount(true);
    window.addEventListener('fl:account', onAccount);
    return () => window.removeEventListener('fl:account', onAccount);
  }, []);

  // Close the user menu on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const close = () => setOpen(false);
  const member = user && !user.isAnonymous;
  const isAdmin = ['admin', 'owner'].includes(role);
  const inProject = /^\/deals\/[^/]+/.test(location.pathname);

  return (
    <nav className={`cs-nav ${scrolled || open ? 'scrolled' : ''} ${open ? 'open' : ''}`}>
      <Link to="/" className="cs-brand-block" onClick={close} aria-label={`${APP_NAME} home`}>
        <span className="cs-brand">{APP_NAME}</span>
        <span className="cs-brand-tagline">Documents. Smarter. Faster.</span>
      </Link>
      <button
        type="button"
        className="cs-nav-burger"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        {open ? '✕' : '☰'}
      </button>

      {/* Centered search — opens the ⌘K palette (route-aware: inside a
          project it focuses that project's embedded panel). */}
      {member && (
        <button
          type="button"
          className="cs-nav-search"
          onClick={() => openCommandBar()}
          title={inProject
            ? 'Ask or instruct this project (⌘K) — stays inside the project'
            : 'Search or ask across your workspace (⌘K)'}
        >
          <Icon name="search" size={16} />
          <span className="cs-nav-search-label">
            {inProject
              ? 'Ask this project, or instruct the drafting agent…'
              : 'Search documents, clauses, projects, or ask a question…'}
          </span>
          <kbd>⌘K</kbd>
        </button>
      )}
      {!member && <div className="cs-nav-spacer" />}

      {/* Mobile menu links (hidden on desktop — the sidebar is the nav). */}
      <div className="cs-nav-links">
        <NavLink to="/" end onClick={close}>Home</NavLink>
        <NavLink to="/projects" onClick={close}>Projects</NavLink>
        {member && <NavLink to="/library" onClick={close}>Library</NavLink>}
        {member && isAdmin && (
          <NavLink to="/activity" onClick={close}>Activity</NavLink>
        )}
        {member && (
          <a
            href="#ask"
            onClick={(e) => { e.preventDefault(); close(); openCommandBar(); }}
          >
            Ask or instruct… ⌘K
          </a>
        )}
      </div>

      {member && (
        <>
          {isAdmin && (
            <button
              type="button"
              className="cs-nav-iconbtn"
              onClick={() => navigate('/activity')}
              title="Agent activity"
              aria-label="Agent activity"
            >
              <Icon name="bell" size={17} />
            </button>
          )}
          <button
            type="button"
            className="cs-nav-iconbtn"
            onClick={() => openGuide()}
            title="Open the platform guide"
            aria-label="Open the platform guide"
          >
            <Icon name="help" size={17} />
          </button>
        </>
      )}

      {user ? (
        <div className="cs-nav-user">
          <div className="cs-nav-chipwrap" ref={menuRef}>
            <button
              type="button"
              className="cs-nav-chip"
              onClick={() => setMenuOpen((m) => !m)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              title="Account"
            >
              <span className="cs-avatar">{initialsOf(user.displayName || user.email)}</span>
              <span className="cs-nav-chip-meta">
                <span className="cs-nav-chip-name">{user.displayName || user.email}</span>
                {org && <span className="cs-nav-chip-org">{org.name}</span>}
              </span>
              <Icon name="chevronDown" size={14} />
            </button>
            {menuOpen && (
              <div className="cs-nav-menu" role="menu">
                <button
                  type="button"
                  className="cs-nav-menu-item"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setShowAccount(true); }}
                >
                  <Icon name="gear" size={16} /> Account settings
                </button>
                {(memberships || []).length > 1 && (
                  <>
                    <div className="cs-nav-menu-sep" />
                    {(memberships || []).map((m) => (
                      <button
                        key={m.orgId}
                        type="button"
                        className="cs-nav-menu-item"
                        role="menuitem"
                        onClick={() => { setOrgId(m.orgId); setMenuOpen(false); }}
                        style={m.orgId === org?.id ? { color: 'var(--surf)' } : undefined}
                      >
                        <Icon name="folder" size={16} />
                        {m.orgId === org?.id && org?.name ? org.name : m.orgId}
                      </button>
                    ))}
                  </>
                )}
                <div className="cs-nav-menu-sep" />
                <button
                  type="button"
                  className="cs-nav-menu-item"
                  role="menuitem"
                  onClick={() => signOut()}
                >
                  <Icon name="key" size={16} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="cs-nav-user">
          <Link to="/login" className="cs-btn cs-btn-outline cs-btn-sm" onClick={close}>
            Sign in
          </Link>
        </div>
      )}
      {showAccount && <AccountModal onClose={() => setShowAccount(false)} />}
    </nav>
  );
}
